import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { paymentService } from '../services';
import cryptoPaymentService from '../services/cryptoPaymentService';
import { getStripePriceId } from '../config/stripe';
import { formatErrorForDisplay } from '../utils/paymentErrorHandler';
import { getCryptoPriceFromConfig } from '../utils/subscriptionPriceUtils';
import { CurrencyCode, ServiceType, PlanDetails } from '../types/api';
import { AddressWithId } from '../services/addressService';

interface PaymentProcessingParams {
  subscription: any;
  paymentType: 'one_time' | 'subscription';
  selectedPaymentMethod: string;
  subscriptionLevel: 'standard' | 'premium';
  subscriptionType: 'monthly' | 'yearly' | 'trial';
  currency: CurrencyCode;
  serviceType: ServiceType;
  planDetails: PlanDetails | null;
  selectedAddress: AddressWithId;
  isGift: boolean;
  calculatedTotal: number;
  finalTotal: number;
  effectivePromotionCode: string | null;
  promotionDetails: any;
  cryptoPricing: any;
  currentUserEmail?: string;
  // Gift info
  recipientName?: string;
  recipientPhone?: string;
  giftMessage?: string;
  senderName?: string;
  senderPhone?: string;
}

export const usePaymentProcessing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const processPayment = useCallback(async (params: PaymentProcessingParams) => {
    const {
      subscription,
      paymentType,
      selectedPaymentMethod,
      subscriptionLevel,
      subscriptionType,
      currency,
      serviceType,
      planDetails,
      selectedAddress,
      isGift,
      calculatedTotal,
      finalTotal,
      effectivePromotionCode,
      promotionDetails,
      cryptoPricing,
      currentUserEmail,
      recipientName,
      recipientPhone,
      giftMessage,
      senderName,
      senderPhone
    } = params;

    // For subscription payment type, use createStripeSubscription (true Stripe subscriptions)
    if (paymentType === 'subscription' && selectedPaymentMethod === 'card') {
      const priceId = getStripePriceId(
        subscriptionLevel, 
        subscriptionType === 'yearly' ? 'yearly' : 'monthly',
        currency,
        serviceType
      );
        
      const stripeSubscription = await paymentService.createStripeSubscription(
        undefined, // Let the backend determine the customer ID
        priceId,
        selectedPaymentMethod,
        { subscription_id: subscription.id },
        effectivePromotionCode || undefined,
        currency
      );
      
      navigate('/stripe-payment', { 
        state: { 
          clientSecret: stripeSubscription.clientSecret,
          subscriptionId: stripeSubscription.subscriptionId,
          paymentType: paymentType,
          subscriptionType: subscriptionType,
          isStripeSubscription: true,
          currency: currency,
          plan: subscriptionLevel,
          amount: finalTotal,
          serviceType: serviceType,
          planDetails: planDetails,
          deliveryCount: subscriptionType === 'monthly' ? 4 : 52,
          addressInfo: selectedAddress,
          isGift: isGift,
          recipientInfo: isGift ? {
            recipientName,
            recipientPhone,
            giftMessage,
            senderName,
            senderPhone
          } : null,
          appliedPromotionCode: effectivePromotionCode,
          promotionDetails: promotionDetails
        } 
      });
      
      return;
    }

    // Handle cryptocurrency payments
    if (selectedPaymentMethod === 'crypto') {
      try {
        toast.loading(t('subscription.loading.settingUpCrypto'), { duration: 3000 });
        
        const cryptoPayment = await cryptoPaymentService.createPayment(
          subscription.id,
          (selectedPaymentMethod === 'crypto' && currency === 'usd' && subscriptionType === 'monthly')
            ? getCryptoPriceFromConfig(subscriptionLevel, serviceType, cryptoPricing)
            : calculatedTotal,
          currency,
          currentUserEmail,
          `${window.location.origin}/payment-success?subscriptionId=${subscription.id}&paymentType=${paymentType}&subscriptionType=${subscriptionType}&currency=${currency}`
        );
        
        if (cryptoPayment && cryptoPayment.paymentUrl) {
          setTimeout(() => {
            window.location.href = cryptoPayment.paymentUrl;
          }, 1000);
          return;
        } else {
          throw new Error('Failed to get payment URL');
        }
      } catch (error) {
        console.error('Error setting up crypto payment:', error);
        const errorInfo = formatErrorForDisplay(error);
        toast.error(`${errorInfo.title}: ${errorInfo.message}`);
        throw error;
      }
    }

    // For one-time payments or promptpay
    const finalAmount = calculatedTotal;
    
    // Debug logging for payment amount tracking
    if (subscriptionType === 'yearly') {
      console.log('💰 Payment Amount Debug Info:', {
        subscriptionType,
        paymentType,
        finalAmount,
        calculatedTotal,
        finalTotal,
        difference: finalAmount - finalTotal,
        expectedYearlyAmount: subscriptionLevel === 'premium' 
          ? (planDetails?.premium?.price || 0) * 12 * 0.9
          : (planDetails?.standard?.price || 0) * 12 * 0.9
      });
    }
    
    // Create a payment intent
    const intent = await paymentService.createPaymentIntent(
      subscription.id,
      finalAmount,
      paymentType,
      selectedPaymentMethod,
      effectivePromotionCode || undefined,
      currency
    );
    
    // Navigate to payment page based on selected payment method
    if (selectedPaymentMethod === 'card') {
      navigate('/stripe-payment', { 
        state: { 
          clientSecret: intent.clientSecret,
          subscriptionId: subscription.id,
          paymentType: paymentType,
          subscriptionType: subscriptionType,
          currency: currency,
          plan: subscriptionLevel,
          amount: finalAmount,
          serviceType: serviceType,
          planDetails: planDetails,
          deliveryCount: subscriptionType === 'monthly' ? 4 : 52,
          addressInfo: selectedAddress,
          isGift: isGift,
          recipientInfo: isGift ? {
            recipientName,
            recipientPhone,
            giftMessage,
            senderName,
            senderPhone
          } : null,
          appliedPromotionCode: effectivePromotionCode,
          promotionDetails: promotionDetails
        } 
      });
    } else if (selectedPaymentMethod === 'qr' || selectedPaymentMethod === 'promptpay') {
      if (!intent.clientSecret) {
        toast.error(t('subscription.errors.paymentSetupFailed', { message: t('subscription.errors.paymentSetupFailed') }));
        return;
      }
      
      navigate('/qr-payment', { 
        state: { 
          clientSecret: intent.clientSecret,
          subscriptionId: subscription.id,
          amount: finalAmount,
          paymentType: paymentType,
          subscriptionType: subscriptionType,
          paymentMethod: selectedPaymentMethod,
          userEmail: selectedPaymentMethod === 'promptpay' ? currentUserEmail : undefined,
          currency: currency,
          plan: subscriptionLevel
        }
      });
    } else {
      navigate('/payment-success', { 
        state: { 
          subscriptionId: subscription.id,
          paymentType: paymentType,
          subscriptionType: subscriptionType,
          currency: currency,
          plan: subscriptionLevel
        } 
      });
    }
  }, [navigate, t]);

  return { processPayment };
}; 